import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ChefHat, Loader2, KeyRound, Mail, CheckCircle, Send, Store, Phone, MapPin, User, MessageSquare } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STORE_TYPES = [
  { value: 'acai', label: 'Açaí e Sorvetes' },
  { value: 'lanches', label: 'Lanches e Hambúrgueres' },
  { value: 'pizzaria', label: 'Pizzaria' },
  { value: 'restaurante', label: 'Restaurante' },
  { value: 'supermercado', label: 'Supermercado' },
  { value: 'farmacia', label: 'Farmácia' },
  { value: 'petshop', label: 'Pet Shop' },
  { value: 'outros', label: 'Outros' }
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoverySent, setRecoverySent] = useState(false);
  
  // Interest form state
  const [showInterestForm, setShowInterestForm] = useState(false);
  const [interestLoading, setInterestLoading] = useState(false);
  const [interestSent, setInterestSent] = useState(false);
  const [interestData, setInterestData] = useState({
    store_name: '',
    store_type: '',
    store_type_other: '',
    owner_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    message: ''
  });
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login realizado com sucesso!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!recoveryEmail.trim()) {
      toast.error('Por favor, informe seu email');
      return;
    }
    
    setRecoveryLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: recoveryEmail
      });
      setRecoverySent(true);
      toast.success('Email enviado!');
    } catch (error) {
      toast.error('Erro ao enviar email');
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleCloseRecovery = () => {
    setShowForgotPassword(false);
    setRecoveryEmail('');
    setRecoverySent(false);
  };

  const handleInterestSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!interestData.store_name.trim()) {
      toast.error('Informe o nome da loja');
      return;
    }
    if (!interestData.store_type) {
      toast.error('Selecione o tipo de delivery');
      return;
    }
    if (interestData.store_type === 'outros' && !interestData.store_type_other.trim()) {
      toast.error('Especifique o tipo de delivery');
      return;
    }
    if (!interestData.owner_name.trim()) {
      toast.error('Informe o nome do responsável');
      return;
    }
    if (!interestData.email.trim()) {
      toast.error('Informe o email');
      return;
    }
    if (!interestData.phone.trim()) {
      toast.error('Informe o telefone');
      return;
    }
    if (!interestData.city.trim()) {
      toast.error('Informe a cidade');
      return;
    }
    
    setInterestLoading(true);
    try {
      await axios.post(`${API}/interest-requests`, interestData);
      setInterestSent(true);
      toast.success('Solicitação enviada com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar solicitação');
    } finally {
      setInterestLoading(false);
    }
  };

  const handleCloseInterest = () => {
    setShowInterestForm(false);
    setInterestSent(false);
    setInterestData({
      store_name: '',
      store_type: '',
      store_type_other: '',
      owner_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      message: ''
    });
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="login-card">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center mb-4">
            <ChefHat className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">Delivery OS</CardTitle>
          <CardDescription>Entre na sua conta para gerenciar seu estabelecimento</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Senha</Label>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-red-500 hover:text-red-400 hover:underline"
                  data-testid="forgot-password-btn"
                >
                  Esqueceu a senha?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-red-500 hover:bg-red-600" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Entrar
            </Button>
          </form>
          
          {/* Interest Form Button */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-center text-sm text-muted-foreground mb-3">
              Quer usar o Delivery OS no seu negócio?
            </p>
            <Button 
              type="button"
              variant="outline" 
              className="w-full border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => setShowInterestForm(true)}
              data-testid="interest-form-btn"
            >
              <Store className="w-4 h-4 mr-2" />
              Quero usar o sistema
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Interest Form Dialog */}
      <Dialog open={showInterestForm} onOpenChange={handleCloseInterest}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="interest-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5 text-red-500" />
              Solicitar Acesso ao Sistema
            </DialogTitle>
            <DialogDescription>
              {interestSent 
                ? 'Recebemos sua solicitação!'
                : 'Preencha o formulário abaixo e entraremos em contato em breve.'}
            </DialogDescription>
          </DialogHeader>
          
          {interestSent ? (
            <div className="py-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Solicitação Enviada!</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Obrigado pelo interesse! Nossa equipe irá analisar sua solicitação e entrará em contato através do email ou telefone informado.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 text-left">
                <p className="font-medium">Próximos passos:</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Aguarde o contato da nossa equipe</li>
                  <li>Após aprovação, você receberá suas credenciais de acesso</li>
                  <li>Configure sua loja e comece a receber pedidos!</li>
                </ul>
              </div>
              <Button 
                className="mt-4 bg-red-500 hover:bg-red-600" 
                onClick={handleCloseInterest}
              >
                Entendi
              </Button>
            </div>
          ) : (
            <form onSubmit={handleInterestSubmit}>
              <div className="space-y-4 py-2">
                {/* Store Name */}
                <div className="space-y-2">
                  <Label htmlFor="store_name">Nome da Loja *</Label>
                  <div className="relative">
                    <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="store_name"
                      placeholder="Ex: Açaí da Val"
                      value={interestData.store_name}
                      onChange={(e) => setInterestData(prev => ({ ...prev, store_name: e.target.value }))}
                      className="pl-10"
                      data-testid="interest-store-name"
                    />
                  </div>
                </div>

                {/* Store Type */}
                <div className="space-y-2">
                  <Label htmlFor="store_type">Tipo de Delivery *</Label>
                  <Select
                    value={interestData.store_type}
                    onValueChange={(value) => setInterestData(prev => ({ ...prev, store_type: value }))}
                  >
                    <SelectTrigger data-testid="interest-store-type">
                      <SelectValue placeholder="Selecione o tipo..." />
                    </SelectTrigger>
                    <SelectContent>
                      {STORE_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Other Store Type */}
                {interestData.store_type === 'outros' && (
                  <div className="space-y-2">
                    <Label htmlFor="store_type_other">Especifique o tipo *</Label>
                    <Input
                      id="store_type_other"
                      placeholder="Ex: Floricultura, Doceria..."
                      value={interestData.store_type_other}
                      onChange={(e) => setInterestData(prev => ({ ...prev, store_type_other: e.target.value }))}
                      data-testid="interest-store-type-other"
                    />
                  </div>
                )}

                {/* Owner Name */}
                <div className="space-y-2">
                  <Label htmlFor="owner_name">Nome do Responsável *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="owner_name"
                      placeholder="Seu nome completo"
                      value={interestData.owner_name}
                      onChange={(e) => setInterestData(prev => ({ ...prev, owner_name: e.target.value }))}
                      className="pl-10"
                      data-testid="interest-owner-name"
                    />
                  </div>
                </div>

                {/* Email and Phone */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="interest_email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="interest_email"
                        type="email"
                        placeholder="email@exemplo.com"
                        value={interestData.email}
                        onChange={(e) => setInterestData(prev => ({ ...prev, email: e.target.value }))}
                        className="pl-10"
                        data-testid="interest-email"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="interest_phone">Telefone/WhatsApp *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="interest_phone"
                        placeholder="(00) 00000-0000"
                        value={interestData.phone}
                        onChange={(e) => setInterestData(prev => ({ ...prev, phone: e.target.value }))}
                        className="pl-10"
                        data-testid="interest-phone"
                      />
                    </div>
                  </div>
                </div>

                {/* Address and City */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="interest_city">Cidade *</Label>
                    <Input
                      id="interest_city"
                      placeholder="Sua cidade"
                      value={interestData.city}
                      onChange={(e) => setInterestData(prev => ({ ...prev, city: e.target.value }))}
                      data-testid="interest-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="interest_address">Endereço</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="interest_address"
                        placeholder="Rua, número..."
                        value={interestData.address}
                        onChange={(e) => setInterestData(prev => ({ ...prev, address: e.target.value }))}
                        className="pl-10"
                        data-testid="interest-address"
                      />
                    </div>
                  </div>
                </div>

                {/* Message */}
                <div className="space-y-2">
                  <Label htmlFor="interest_message">Mensagem (opcional)</Label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <Textarea
                      id="interest_message"
                      placeholder="Conte-nos mais sobre seu negócio..."
                      value={interestData.message}
                      onChange={(e) => setInterestData(prev => ({ ...prev, message: e.target.value }))}
                      className="pl-10 min-h-[80px]"
                      data-testid="interest-message"
                    />
                  </div>
                </div>
              </div>
              
              <DialogFooter className="mt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCloseInterest}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-red-500 hover:bg-red-600"
                  disabled={interestLoading}
                  data-testid="submit-interest-btn"
                >
                  {interestLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Enviar Solicitação
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={handleCloseRecovery}>
        <DialogContent data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-red-500" />
              Recuperar Senha
            </DialogTitle>
            <DialogDescription>
              {recoverySent 
                ? 'Verifique sua caixa de entrada.'
                : 'Informe seu email para receber o link de recuperação.'}
            </DialogDescription>
          </DialogHeader>
          
          {recoverySent ? (
            <div className="py-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Email Enviado!</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Se o email estiver cadastrado, você receberá um link para redefinir sua senha.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 text-left">
                <p className="font-medium">Não recebeu o email?</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Verifique a pasta de spam/lixo eletrônico</li>
                  <li>Aguarde alguns minutos e tente novamente</li>
                  <li>Certifique-se de que o email está correto</li>
                </ul>
              </div>
              <Button 
                className="mt-4" 
                onClick={handleCloseRecovery}
              >
                Entendi
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="recovery-email">Email da conta</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="recovery-email"
                      type="email"
                      placeholder="seu@email.com"
                      value={recoveryEmail}
                      onChange={(e) => setRecoveryEmail(e.target.value)}
                      className="pl-10"
                      data-testid="recovery-email-input"
                    />
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <p className="font-medium flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Como funciona?
                  </p>
                  <p className="mt-1">
                    Enviaremos um link para seu email. Clique no link para criar uma nova senha. O link expira em 1 hora.
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={handleCloseRecovery}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  className="bg-red-500 hover:bg-red-600"
                  disabled={recoveryLoading}
                  data-testid="submit-recovery-btn"
                >
                  {recoveryLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Enviar Link
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
